import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CalendarDays, Clock } from "lucide-react";
import { useLanguage } from "@/i18n";
import { articles, localizeArticle } from "@/data/articles";
import Seo from "@/components/Seo";

export default function BlogIndex() {
  const { lang, t } = useLanguage();
  return (
    <main className="pt-24 sm:pt-28 pb-24 bg-[#0B1220] blueprint-grid min-h-screen" data-testid="blog-index">
      <Seo
        title="Artikel & Panduan Pengadaan Kayu — CV. Sinar Al Mubarokah"
        description="Panduan lengkap seputar harga kayu, jenis kayu olahan, dan tips membeli langsung dari pabrik kayu di Jakarta. Artikel untuk kontraktor dan industri."
        path="/artikel"
      />
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl">
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-sky-400 font-semibold">{t.blog.eyebrow}</p>
          <h1 className="mt-4 font-display font-extrabold tracking-tight text-white text-2xl sm:text-3xl lg:text-4xl">{t.blog.title}</h1>
          <p className="mt-4 text-slate-400 text-base sm:text-lg">{t.blog.sub}</p>
        </motion.div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles.map((raw, i) => {
            const a = localizeArticle(raw, lang);
            return (
              <motion.article
                key={a.slug}
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
                data-testid={`article-card-${a.slug}`}
                className="group bg-[#101B2E] rounded-2xl border border-slate-800 overflow-hidden hover:border-sky-500/60 hover:shadow-[0_24px_50px_-20px_rgba(14,116,144,0.45)] transition-all flex flex-col"
              >
                <Link to={`/artikel/${a.slug}`} className="block aspect-[16/10] overflow-hidden">
                  <img src={a.img} alt={a.title} loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </Link>
                <div className="p-6 flex flex-col flex-1">
                  <span className="self-start bg-sky-500/10 border border-sky-500/30 text-sky-300 font-mono text-[10px] tracking-wider px-3 py-1 rounded-full">
                    {a.keyword}
                  </span>
                  <Link to={`/artikel/${a.slug}`}>
                    <h2 className="mt-3 font-display font-bold text-slate-100 text-base sm:text-lg leading-snug group-hover:text-sky-400 transition-colors">
                      {a.title}
                    </h2>
                  </Link>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed flex-1">{a.excerpt}</p>
                  <div className="mt-4 pt-4 border-t border-slate-800 flex items-center justify-between text-xs text-slate-500">
                    <span className="flex items-center gap-1.5"><CalendarDays size={13} /> {a.date}</span>
                    <span className="flex items-center gap-1.5"><Clock size={13} /> {a.minutes} {t.blog.min}</span>
                  </div>
                  <Link to={`/artikel/${a.slug}`} data-testid={`read-article-${a.slug}`}
                    className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-sky-400 hover:text-sky-300 transition-colors">
                    {t.blog.readMore} <ArrowRight size={15} />
                  </Link>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </main>
  );
}
